import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";
import { formatHskLevels, formatSessionWhen, missedCardsFrom, sessionScoreParts } from "../helpers/progress";
import { SavedTestResult } from "../helpers/storage";
import { Details, HskEntry } from "../helpers/types";
import { menuColors, menuTheme } from "../theme/menuTheme";
import DetailCheckboxes from "./DetailCheckboxes";
import HSKLevelCheckboxes from "./HSKLevelCheckboxes";
import LengthOptions from "./LengthOptions";
import OrderButtons from "./OrderButtons";
import TestSessionDetail from "./TestSessionDetail";

export type LastTestResult = SavedTestResult;

const sectionTitleSx = {
  fontWeight: 600,
  color: menuColors.text,
  textAlign: "left" as const
};

const labelSx = {
  mb: 1,
  textAlign: "left" as const,
  color: menuColors.textMuted
};

const LastTestCard = ({
  result,
  onOpen,
  onRetryMissed
}: {
  result: SavedTestResult;
  onOpen: () => void;
  onRetryMissed: (cards: HskEntry[]) => void;
}) => {
  const { right, wrong, pct } = sessionScoreParts(result.cards, result.answers);
  const missed = missedCardsFrom(result.cards, result.answers);
  const when = result.finishedAt ? formatSessionWhen(result.finishedAt) : "Last test";
  const levels = formatHskLevels(result.filteredLevels ?? []);

  return (
    <Box sx={{ mb: 3 }}>
      <Button
        fullWidth
        onClick={onOpen}
        sx={{
          display: "block",
          textAlign: "left",
          textTransform: "none",
          p: 1.5,
          border: `1px solid ${menuColors.border}`,
          borderRadius: 2,
          backgroundColor: menuColors.surfaceRaised,
          color: menuColors.text,
          "&:hover": {
            borderColor: menuColors.accent,
            backgroundColor: menuColors.surfaceRaised
          }
        }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
          <Typography sx={{ fontSize: 13, color: menuColors.textMuted }}>Last test</Typography>
          <Typography sx={{ fontSize: 12, color: menuColors.textMuted }}>{when}</Typography>
        </Box>
        <Typography sx={{ fontSize: 28, fontWeight: 700, color: menuColors.accent, lineHeight: 1.1 }}>
          {pct}%
        </Typography>
        <Typography sx={{ fontSize: 13, color: menuColors.textMuted, mt: 0.5 }}>
          {right} right · {wrong} wrong
          {levels !== "HSK" ? ` · ${levels}` : ""}
        </Typography>
      </Button>
      {missed.length > 0 ? (
        <Button
          fullWidth
          variant="outlined"
          onClick={() => onRetryMissed(missed)}
          sx={{ mt: 1, textTransform: "none", fontWeight: 600 }}>
          {`Retry the ${missed.length} missed`}
        </Button>
      ) : null}
    </Box>
  );
};

const TestSetup = ({
  filteredLevels,
  setFilteredLevels,
  cardAmount,
  setCardAmount,
  maxCards,
  detailsOptions,
  setDetailsOptions,
  shuffled,
  onRandomize,
  onReorder,
  onStartTest,
  onStartCustomTest,
  lastTestResult
}: {
  filteredLevels: number[];
  setFilteredLevels: (levels: number[]) => void;
  cardAmount: number;
  setCardAmount: (value: number) => void;
  maxCards: number;
  detailsOptions: Details;
  setDetailsOptions: (details: Details) => void;
  shuffled: boolean;
  onRandomize: () => void;
  onReorder: () => void;
  onStartTest: () => void;
  onStartCustomTest: (cards: HskEntry[]) => void;
  lastTestResult?: LastTestResult | null;
}) => {
  const [showSession, setShowSession] = useState(false);

  return (
    <ThemeProvider theme={menuTheme}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          textAlign: "left",
          backgroundColor: menuColors.surface
        }}>
        {showSession && lastTestResult ? (
          <TestSessionDetail
            result={lastTestResult}
            onBack={() => setShowSession(false)}
            onRetryMissed={onStartCustomTest}
            onRetryTest={onStartCustomTest}
          />
        ) : (
          <>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2, pt: 2, pb: 1 }}>
              {lastTestResult && lastTestResult.cards.length > 0 ? (
                <LastTestCard
                  result={lastTestResult}
                  onOpen={() => setShowSession(true)}
                  onRetryMissed={onStartCustomTest}
                />
              ) : null}
              <Typography variant="h6" sx={{ ...sectionTitleSx, mb: 0.5 }}>
                New test
              </Typography>
              <Typography variant="body2" sx={{ ...labelSx, mb: 2 }}>
                Pick the deck, then start. One card at a time, with a score.
              </Typography>

              <Typography variant="body2" sx={{ ...sectionTitleSx, mb: 1 }}>
                HSK level
              </Typography>
              <HSKLevelCheckboxes filteredLevels={filteredLevels} setFilteredLevels={setFilteredLevels} />

              <Typography variant="body2" sx={{ ...sectionTitleSx, mt: 2, mb: 1 }}>
                Length
              </Typography>
              <LengthOptions max={maxCards} value={cardAmount} onChange={setCardAmount} />

              <Typography variant="body2" sx={{ ...sectionTitleSx, mt: 2, mb: 1 }}>
                Order
              </Typography>
              <OrderButtons shuffled={shuffled} onRandomize={onRandomize} onReorder={onReorder} />

              <Typography variant="body2" sx={{ ...sectionTitleSx, mt: 2, mb: 1 }}>
                Card details
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ ...sectionTitleSx, fontSize: 14 }}>
                    Front
                  </Typography>
                  <DetailCheckboxes
                    detailsOptions={detailsOptions}
                    setDetailsOptions={setDetailsOptions}
                    side="front"
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ ...sectionTitleSx, fontSize: 14 }}>
                    Back
                  </Typography>
                  <DetailCheckboxes
                    detailsOptions={detailsOptions}
                    setDetailsOptions={setDetailsOptions}
                    side="back"
                  />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                px: 2,
                pt: 1,
                pb: 1.5,
                borderTop: `1px solid ${menuColors.border}`,
                backgroundColor: menuColors.surface
              }}>
              <Button variant="contained" fullWidth disabled={maxCards === 0} onClick={onStartTest}>
                Start test
              </Button>
            </Box>
          </>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default TestSetup;
